/** @jsx jsx */
import { jsx } from '@emotion/core';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useMediaQuery, useTheme } from '@material-ui/core';

import {
  challengeSelector,
  challengeResultsSelector,
} from 'reducers/challenge.reducer';
import UsersPhoto from 'components/widgets/WidgetAction/Results/PhotoResult/UsersPhoto';
import { PrimaryButton, TertiaryButton } from 'components';
import { ICustomTheme, FileTypes } from 'interfaces';
import { LOADING_STATUS } from 'constants/status';
import * as S from './styles';

type AddPhotoModalType = {
  challengeId: number;
  onSubmit: () => void;
  open?: boolean;
  resetUserPhoto: () => void;
  setOpenModal: (value: boolean) => void;
  userPhoto: FileTypes;
};

const AddPhotoModal = ({
  challengeId,
  onSubmit,
  open = false,
  resetUserPhoto,
  setOpenModal,
  userPhoto,
}: AddPhotoModalType): JSX.Element | null => {
  const { t } = useTranslation();
  const theme: ICustomTheme = useTheme();
  const isDownSm = useMediaQuery(theme.breakpoints.down('sm'));
  const didMountRef = useRef(false);
  const { error } = useSelector(challengeSelector(challengeId));
  const { status: loadingStatus } = useSelector(
    challengeResultsSelector(challengeId),
  );

  useEffect(() => {
    if (didMountRef.current && loadingStatus === LOADING_STATUS.RESOLVED) {
      setOpenModal(false);
    } else {
      didMountRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingStatus]);

  return (
    <S.Modal open={open}>
      <S.Container>
        <S.Title variant={isDownSm ? 'h4' : 'h3'}>
          {t('photo_contest.results.undefined_status.add_photo_modal.title')}
        </S.Title>

        <S.Description variant={isDownSm ? 'body2' : 'h4'}>
          {t('photo_contest.results.undefined_status.add_photo_modal.warning')}
        </S.Description>

        <UsersPhoto
          imageUrl={userPhoto.preview}
          size="large"
          status="secondary"
        />

        <S.ButtonsContainer>
          <TertiaryButton
            btnType="button"
            color="secondaryInverted"
            onClick={() => {
              resetUserPhoto();
              setOpenModal(false);
            }}
          >
            {t('core.cancel')}
          </TertiaryButton>

          <PrimaryButton btnType="button" color="primary" onClick={onSubmit}>
            {loadingStatus === LOADING_STATUS.PENDING ? (
              <S.StyledCircularProgress size={20} />
            ) : (
              t('core.submit')
            )}
          </PrimaryButton>
        </S.ButtonsContainer>

        {loadingStatus === LOADING_STATUS.REJECTED && (
          <S.ErrorMessage>
            {error?.response?.status === 400
              ? t('photo_contest.results.refused_status.error_400')
              : t('core.loading_error')}
          </S.ErrorMessage>
        )}
      </S.Container>
    </S.Modal>
  );
};

export default AddPhotoModal;
